import {
    Box, Button,
    FormControl, MenuItem, Select, Slider,
    Stack, TextField,
    Typography,
    Tabs, Tab,
    useMediaQuery,
} from "@mui/material";
import {
    PaletteOutlined,
    ImageOutlined,
    AspectRatioOutlined,
    ColorLensOutlined,
    FileDownloadOutlined,
} from "@mui/icons-material";
import {useEffect, useState} from "react";
import QrColorFields, {DEFAULT_COLOR_FIELDS} from "./QrColorFields";
import OptionSection from "./OptionSection";
import LogoField, {DEFAULT_LOGO_FIELD} from "./LogoField";
import SectionedMobileNav from "./SectionedMobileNav";
import {listPalettes, createPalette} from "../../Utils/palettesApi";

const BulleSlide = {
    '& .MuiSlider-valueLabel': {
        backgroundColor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: '20px',
        height: '20px',
        width: '50px',
        fontSize: '12px'
    }
};

const sliderValueLabelPixel = (value) => `${value} px`;

function extractColorFields(source) {
    const fields = {};
    Object.keys(DEFAULT_COLOR_FIELDS).forEach((key) => {
        if (source[key] !== undefined) fields[key] = source[key];
    });
    return fields;
}

/**
 * Logique + JSX des options de style d'un QR code, exposée sous plusieurs formes :
 * - basicFields / advancedFields : Style à côté d'un aperçu (desktop) + reste en pleine largeur.
 * - mobileNav : barre d'onglets fixée en bas de l'écran + tiroir avec les options de la section sélectionnée,
 *   pensé pour un agencement mobile où l'URL et l'aperçu doivent rester visibles ensemble en haut de l'écran.
 * Remonte l'objet de style composé (aligné avec les champs attendus par le backend) via onChange.
 */
export function useQrStyleForm(initialValue = {}, onChange) {
    const isDesktop = useMediaQuery('(min-width:900px)');

    const [qrCodeSize, setQrCodeSize] = useState(initialValue.width || 400);
    const [compression, setCompression] = useState(initialValue.errorCorrectionLevel || 'M');
    const [logoField, setLogoField] = useState({
        logo: initialValue.logo || null,
        logoSize: initialValue.logoSize || DEFAULT_LOGO_FIELD.logoSize,
    });
    const [colorFields, setColorFields] = useState({...DEFAULT_COLOR_FIELDS, ...extractColorFields(initialValue)});
    const [outputFormat, setOutputFormat] = useState(initialValue.format || 'png');

    const [palettes, setPalettes] = useState([]);
    const [selectedPaletteId, setSelectedPaletteId] = useState('');
    const [savingPalette, setSavingPalette] = useState(false);
    const [newPaletteName, setNewPaletteName] = useState('');

    const [activeTab, setActiveTab] = useState('logo');

    useEffect(() => {
        listPalettes().then(setPalettes).catch((error) => console.error('Erreur lors du chargement des palettes:', error));
    }, []);

    const handleSelectPalette = (paletteId) => {
        setSelectedPaletteId(paletteId);
        if (!paletteId) {
            setColorFields({...DEFAULT_COLOR_FIELDS});
            return;
        }
        const palette = palettes.find((p) => p.id === paletteId);
        if (palette) {
            setColorFields({...DEFAULT_COLOR_FIELDS, ...palette.style});
            setLogoField({
                logo: palette.style.logo || null,
                logoSize: palette.style.logoSize || DEFAULT_LOGO_FIELD.logoSize,
            });
        }
    };

    const handleSaveAsPalette = async () => {
        if (!newPaletteName.trim()) return;

        try {
            const style = {
                ...colorFields,
                ...(logoField.logo ? {logo: logoField.logo, logoSize: logoField.logoSize} : {}),
            };
            const palette = await createPalette({name: newPaletteName, style});
            setPalettes([...palettes, palette]);
            setSelectedPaletteId(palette.id);
            setNewPaletteName('');
            setSavingPalette(false);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de la palette:', error);
            alert('Erreur lors de la sauvegarde de la palette');
        }
    };

    useEffect(() => {
        onChange({
            width: qrCodeSize,
            height: qrCodeSize,
            format: outputFormat,
            errorCorrectionLevel: logoField.logo && compression === 'L' ? 'M' : compression,
            ...(logoField.logo ? {logo: logoField.logo, logoSize: logoField.logoSize} : {}),
            ...colorFields,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qrCodeSize, outputFormat, compression, logoField, colorFields]);

    const styleContent = (
        <>
            <FormControl fullWidth>
                <Select
                    displayEmpty
                    value={selectedPaletteId}
                    onChange={(e) => handleSelectPalette(e.target.value)}
                >
                    <MenuItem value="">Personnalisé</MenuItem>
                    {palettes.map((palette) => (
                        <MenuItem key={palette.id} value={palette.id}>{palette.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {!savingPalette ? (
                <Button variant="outlined" size="small" onClick={() => setSavingPalette(true)} sx={{mt: 2}}>
                    Enregistrer comme nouvelle palette
                </Button>
            ) : (
                <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 2}}>
                    <TextField
                        size="small"
                        label="Nom de la palette"
                        value={newPaletteName}
                        onChange={(e) => setNewPaletteName(e.target.value)}
                        fullWidth
                    />
                    <Button variant="contained" size="small" onClick={handleSaveAsPalette}>Enregistrer</Button>
                    <Button size="small" onClick={() => setSavingPalette(false)}>Annuler</Button>
                </Stack>
            )}
        </>
    );

    const basicFields = (
        <Stack spacing={6} width="100%">
            <Box width="100%">
                <Typography variant="h6">Style :</Typography>
                {styleContent}
            </Box>
        </Stack>
    );

    const logoContent = <LogoField value={logoField} onChange={setLogoField}/>;

    const sizeContent = (
        <Stack direction="row" spacing={2} sx={{justifyContent: "center", alignItems: "center"}}>
            <Slider
                sx={BulleSlide}
                value={qrCodeSize}
                valueLabelDisplay="auto"
                aria-labelledby="input-slider"
                onChange={(e) => setQrCodeSize(e.target.value)}
                getAriaValueText={sliderValueLabelPixel}
                valueLabelFormat={sliderValueLabelPixel}
                min={200}
                max={800}
            />
            <Box sx={{bgcolor: 'primary.main', borderRadius: '20px', width: '70px', textAlign: 'center'}}>
                <Typography sx={{color: 'primary.contrastText'}}>{qrCodeSize} px</Typography>
            </Box>
        </Stack>
    );

    const colorsContent = (
        <QrColorFields value={colorFields} onChange={(patch) => setColorFields({...colorFields, ...patch})}/>
    );

    const exportContent = (
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
            <FormControl fullWidth>
                <Select value={outputFormat} onChange={(e) => setOutputFormat(e.target.value)}>
                    <MenuItem value="png">PNG</MenuItem>
                    <MenuItem value="jpeg">JPEG</MenuItem>
                    <MenuItem value="svg">SVG</MenuItem>
                </Select>
            </FormControl>
            <FormControl fullWidth>
                <Select value={compression} onChange={(e) => setCompression(e.target.value)}>
                    <MenuItem value="L">Correction faible (7%)</MenuItem>
                    <MenuItem value="M">Correction moyenne (15%)</MenuItem>
                    <MenuItem value="Q">Correction élevée (25%)</MenuItem>
                    <MenuItem value="H">Correction très élevée (30%)</MenuItem>
                </Select>
            </FormControl>
        </Stack>
    );

    // Sections "avancées" (hors Style, qui reste à côté de l'aperçu sur desktop).
    const sections = [
        {key: 'logo', label: 'Logo', node: logoContent, bare: false, icon: <ImageOutlined/>},
        {key: 'size', label: 'Taille', node: sizeContent, bare: false, icon: <AspectRatioOutlined/>},
        {key: 'colors', label: 'Couleurs', node: colorsContent, bare: true, icon: <ColorLensOutlined/>},
        {key: 'export', label: 'Export', node: exportContent, bare: false, icon: <FileDownloadOutlined/>},
    ];

    const currentSection = sections.find((s) => s.key === activeTab) || sections[0];

    const advancedFields = isDesktop ? (
        <Stack spacing={2.5} width="100%">
            {sections.map((s) => (
                s.bare
                    ? <Box key={s.key}>{s.node}</Box>
                    : <OptionSection key={s.key} title={s.label}>{s.node}</OptionSection>
            ))}
        </Stack>
    ) : (
        <Box width="100%">
            <Tabs
                value={currentSection.key}
                onChange={(e, value) => setActiveTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}
            >
                {sections.map((s) => <Tab key={s.key} value={s.key} label={s.label}/>)}
            </Tabs>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                {currentSection.node}
            </Box>
        </Box>
    );

    // Toutes les sections (Style incluse) pour la navigation mobile en tiroir.
    const allSections = [
        {key: 'style', label: 'Style', node: styleContent, icon: <PaletteOutlined/>},
        ...sections,
    ];

    // Barre d'onglets fixée en bas de l'écran + tiroir : pensé pour un agencement mobile où
    // l'URL et l'aperçu du QR doivent rester visibles ensemble, sans les options entre les deux.
    const mobileNav = <SectionedMobileNav sections={allSections}/>;

    return {isDesktop, basicFields, advancedFields, mobileNav};
}

/**
 * Formulaire réutilisable des options de style d'un QR code (basicFields + advancedFields empilés).
 * Pour un agencement personnalisé (ex. deux colonnes avec aperçu + barre mobile), utiliser useQrStyleForm directement.
 */
function QrStyleForm({initialValue = {}, onChange}) {
    const {basicFields, advancedFields} = useQrStyleForm(initialValue, onChange);

    return (
        <Stack spacing={6} width="100%">
            {basicFields}
            {advancedFields}
        </Stack>
    );
}

export default QrStyleForm;
